import { memo } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { NotificationWithActor, NotificationType } from '@/lib/api'
import { Heart, MessageCircle, UserPlus, AtSign, Users, Calendar, Bell } from 'lucide-react'
import { getInitials } from '@/lib/utils'

interface NotificationItemProps {
  notification: NotificationWithActor
  onMarkAsRead?: (notificationId: string) => void
}

const typeIcons: Record<NotificationType, React.ReactNode> = {
  post_reaction: <Heart className="h-4 w-4 text-[#FF2D7B]" fill="currentColor" />,
  post_comment: <MessageCircle className="h-4 w-4 text-[#00D4FF]" />,
  comment_reply: <MessageCircle className="h-4 w-4 text-[#00D4FF]" />,
  friend_request: <UserPlus className="h-4 w-4 text-[#6B3FA0]" />,
  friend_accepted: <UserPlus className="h-4 w-4 text-[#6B3FA0]" fill="currentColor" />,
  new_follower: <UserPlus className="h-4 w-4 text-[#6B3FA0]" />,
  mention: <AtSign className="h-4 w-4 text-[#FF2D7B]" />,
  new_message: <MessageCircle className="h-4 w-4 text-[#00D4FF]" />,
  group_invite: <Users className="h-4 w-4 text-[#6B3FA0]" />,
  group_join: <Users className="h-4 w-4 text-[#6B3FA0]" />,
  group_post: <Users className="h-4 w-4 text-[#6B3FA0]" />,
  event_invite: <Calendar className="h-4 w-4 text-[#FF2D7B]" />,
  event_update: <Calendar className="h-4 w-4 text-[#FF2D7B]" />,
  event_reminder: <Calendar className="h-4 w-4 text-[#FF2D7B]" />,
  ai_job_started: <Bell className="h-4 w-4 text-[#00D4FF]" />,
  ai_job_complete: <Bell className="h-4 w-4 text-[#00D4FF]" />,
  ai_job_failed: <Bell className="h-4 w-4 text-red-500" />,
  system_announce: <Bell className="h-4 w-4 text-[#00D4FF]" />,
  security_alert: <Bell className="h-4 w-4 text-red-500" />,
  birthday_reminder: <Calendar className="h-4 w-4 text-[#FF2D7B]" />,
}

/**
 * NotificationItem component - Memoized for performance in lists
 */
const NotificationItem = memo(function NotificationItem({
  notification,
  onMarkAsRead,
}: NotificationItemProps) {
  const navigate = useNavigate()
  const { notification: notif, actor } = notification

  const handleClick = () => {
    // Mark as read
    if (!notif.is_read) {
      onMarkAsRead?.(notif.id)
    }

    // Navigate based on action URL or notification type
    if (notif.action_url) {
      navigate(notif.action_url)
    } else if (notif.post_id) {
      navigate(`/posts/${notif.post_id}`)
    } else if (notif.comment_id && notif.post_id) {
      navigate(`/posts/${notif.post_id}#comment-${notif.comment_id}`)
    } else if (notif.group_id) {
      navigate(`/groups/${notif.group_id}`)
    } else if (notif.event_id) {
      navigate(`/events/${notif.event_id}`)
    } else if (notif.message_id) {
      navigate('/messages')
    } else if (actor) {
      navigate(`/profile/${actor.username}`)
    }
  }

  return (
    <div
      className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${
        notif.is_read
          ? 'bg-transparent hover:bg-[#1E1E28]/50'
          : 'bg-[#FF2D7B]/5 hover:bg-[#FF2D7B]/10'
      }`}
      onClick={handleClick}
    >
      <div className="relative flex-shrink-0">
        <Avatar className="h-10 w-10 border-2 border-[#1E1E28]">
          <AvatarImage
            src={actor?.avatarUrl || notif.image_url}
            alt={actor?.displayName || 'Notification'}
          />
          <AvatarFallback className="bg-[#1E1E28] text-[#FAFAFA]">
            {actor ? getInitials(actor.displayName) : 'K'}
          </AvatarFallback>
        </Avatar>
        <div className="absolute -bottom-1 -right-1 p-1 rounded-full bg-[#12121A] border border-[#1E1E28]">
          {typeIcons[notif.notification_type] || typeIcons.system_announce}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#FAFAFA]">
          {actor && (
            <span className="font-semibold gradient-text">
              {actor.displayName}
            </span>
          )}{' '}
          <span className="text-[#A0A0B0]">
            {notif.body || notif.title}
          </span>
        </p>
        <span className="text-xs text-[#64647A]">
          {formatDistanceToNow(new Date(notif.created_at), {
            addSuffix: true,
          })}
        </span>
      </div>

      {!notif.is_read && (
        <div className="w-2 h-2 rounded-full bg-[#FF2D7B] flex-shrink-0 mt-2" />
      )}
    </div>
  )
})

export default NotificationItem
